import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import type { AnalyticsDigestEntry } from '../../types/analytics';
import { useAnalyticsDigestMutation } from '../../pages/websiteAnalytics.queries';

interface DigestArchiveProps {
  digests: AnalyticsDigestEntry[];
  digestsLoading: boolean;
}

const DigestArchive: React.FC<DigestArchiveProps> = ({ digests, digestsLoading }) => {
  const [digestViewerOpen, setDigestViewerOpen] = useState(false);
  const [digestViewerContent, setDigestViewerContent] = useState<AnalyticsDigestEntry | null>(null);

  const digestMutation = useAnalyticsDigestMutation();
  const digestViewerLoading = digestMutation.isPending;

  const openDigestViewer = (id: string) => {
    setDigestViewerOpen(true);
    setDigestViewerContent(null);
    digestMutation.mutate(id, {
      onSuccess: (full) => setDigestViewerContent(full),
    });
  };

  const closeDigestViewer = () => {
    setDigestViewerOpen(false);
    setDigestViewerContent(null);
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ArchiveOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Weekly Analytics Reports
        </Typography>
      </Box>
      {digestsLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[0, 1].map((i) => (
            <Skeleton key={i} height={36} />
          ))}
        </Box>
      ) : digests.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Week</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {digests.map((d) => (
                <TableRow
                  key={d.id}
                  hover
                  onClick={() => openDigestViewer(d.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{d.week_start}</TableCell>
                  <TableCell>{d.subject}</TableCell>
                  <TableCell>
                    {d.sent_at ? new Date(d.sent_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No reports yet — weekly analytics emails will appear here once configured.
        </Typography>
      )}

      {/* Digest Viewer Dialog */}
      <Dialog open={digestViewerOpen} onClose={closeDigestViewer} fullWidth maxWidth="md">
        <DialogTitle>{digestViewerContent?.subject ?? 'Loading digest…'}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: 400 }}>
          {digestViewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : digestViewerContent?.html_content ? (
            <iframe
              srcDoc={digestViewerContent.html_content}
              title="Digest preview"
              style={{ width: '100%', height: '70vh', border: 'none' }}
              sandbox=""
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              No email content available for this digest.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDigestViewer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DigestArchive;
